// ReceiptThumb — a small authed preview of a receipt's stored file, for list rows
// that need to be told apart by their picture (failed reads carry no vendor, and
// three of them look identical as text). The image proxy wants the Bearer header,
// so a plain <img src> cannot fetch it; one blob fetch per visible thumb instead.

import React, { useEffect, useState } from 'react';
import { apiFetchAuthed } from '../apiHelpers';

interface Props {
  pid: string;
  mime?: string | null;
  className?: string;
}

export default function ReceiptThumb({ pid, mime, className = '' }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const isPdf = (mime || '').includes('pdf');

  useEffect(() => {
    if (isPdf) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const resp = (await apiFetchAuthed(`/receipts/${pid}/image`, { skipJsonParse: true, silent: true })) as Response;
        if (!resp.ok) throw new Error(String(resp.status));
        const blob = await resp.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [pid, isPdf]);

  const frame = `shrink-0 w-11 h-11 rounded-md bg-surface-container-highest ring-1 ring-inset ring-outline-variant overflow-hidden flex items-center justify-center ${className}`;
  if (isPdf) return <div className={frame}><span className="text-[10px] font-semibold text-on-surface-variant">PDF</span></div>;
  // A stored type this browser cannot draw (a HEIC in Chrome) lands here, not as a broken icon.
  if (url) return <div className={frame}><img src={url} alt="" className="w-full h-full object-cover" onError={() => { setUrl(null); setFailed(true); }} /></div>;
  if (failed) return <div className={frame}><span className="text-[10px] text-on-surface-variant">No image</span></div>;
  return <div className={`${frame} animate-pulse motion-reduce:animate-none`} />;
}
